/**
 * Moon Phase Utilities
 *
 * Calculate and display moon phases for calendar views.
 * Based on synodic month (lunar month) of approximately 29.53 days.
 */

export type MoonPhaseName =
  | 'new'
  | 'waxingCrescent'
  | 'firstQuarter'
  | 'waxingGibbous'
  | 'full'
  | 'waningGibbous'
  | 'lastQuarter'
  | 'waningCrescent';

export interface MoonPhase {
  name: MoonPhaseName;
  label: string;
  icon: string;
  illumination: number; // 0-100%
}

/**
 * Moon phase emoji icons
 */
export const MOON_PHASE_ICONS: Record<MoonPhaseName, string> = {
  new: '🌑',
  waxingCrescent: '🌒',
  firstQuarter: '🌓',
  waxingGibbous: '🌔',
  full: '🌕',
  waningGibbous: '🌖',
  lastQuarter: '🌗',
  waningCrescent: '🌘',
};

/**
 * Moon phase labels (human-readable)
 */
export const MOON_PHASE_LABELS: Record<MoonPhaseName, string> = {
  new: 'New Moon',
  waxingCrescent: 'Waxing Crescent',
  firstQuarter: 'First Quarter',
  waxingGibbous: 'Waxing Gibbous',
  full: 'Full Moon',
  waningGibbous: 'Waning Gibbous',
  lastQuarter: 'Last Quarter',
  waningCrescent: 'Waning Crescent',
};

/**
 * Calculate moon phase for a given date
 *
 * Algorithm:
 * 1. Calculate days since a known new moon
 * 2. Take modulo by synodic month length (29.53 days)
 * 3. Map to phase based on position in cycle
 *
 * @param date - Date to calculate moon phase for
 * @returns Moon phase information
 */
export function getMoonPhase(date: Date): MoonPhase {
  // Known new moon: January 11, 2024 at 11:57 UTC
  const knownNewMoon = new Date('2024-01-11T11:57:00Z');

  // Synodic month (average lunar month duration)
  const synodicMonth = 29.53058770576;

  // Calculate days since known new moon
  const daysSinceNew =
    (date.getTime() - knownNewMoon.getTime()) / (1000 * 60 * 60 * 24);

  // Get position in current lunar cycle (0-29.53 days)
  const phase = ((daysSinceNew % synodicMonth) + synodicMonth) % synodicMonth;

  // Calculate illumination percentage
  const illumination = Math.round((1 - Math.cos((phase / synodicMonth) * 2 * Math.PI)) * 50);

  // Determine phase name based on position
  let name: MoonPhaseName;

  if (phase < 1.84566) {
    name = 'new';
  } else if (phase < 7.38264) {
    name = 'waxingCrescent';
  } else if (phase < 9.22830) {
    name = 'firstQuarter';
  } else if (phase < 14.76528) {
    name = 'waxingGibbous';
  } else if (phase < 16.61094) {
    name = 'full';
  } else if (phase < 22.14792) {
    name = 'waningGibbous';
  } else if (phase < 23.99358) {
    name = 'lastQuarter';
  } else {
    name = 'waningCrescent';
  }

  return {
    name,
    label: MOON_PHASE_LABELS[name],
    icon: MOON_PHASE_ICONS[name],
    illumination,
  };
}

/**
 * Get next occurrence of a specific moon phase
 *
 * @param fromDate - Starting date
 * @param phaseName - Target phase name
 * @returns Date of next occurrence
 */
export function getNextPhase(fromDate: Date, phaseName: MoonPhaseName): Date {
  const synodicMonth = 29.53058770576;
  const startPhase = getMoonPhase(fromDate);

  // Phase targets (in days from new moon)
  const phaseTargets: Record<MoonPhaseName, number> = {
    new: 0,
    waxingCrescent: 3.69132,
    firstQuarter: 7.38264,
    waxingGibbous: 11.07396,
    full: 14.76528,
    waningGibbous: 18.45660,
    lastQuarter: 22.14792,
    waningCrescent: 25.83924,
  };

  const currentPosition = phaseTargets[startPhase.name];
  const targetPosition = phaseTargets[phaseName];

  let daysUntilTarget: number;
  if (targetPosition > currentPosition) {
    daysUntilTarget = targetPosition - currentPosition;
  } else {
    daysUntilTarget = synodicMonth - currentPosition + targetPosition;
  }

  return new Date(fromDate.getTime() + daysUntilTarget * 24 * 60 * 60 * 1000);
}
